'use client';

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { Badge } from '@/components/ui/data-display/badge';
import { Chip } from './Chip';
import { FormSectionHeader } from './FormSectionHeader';
import { cn } from '@/lib/utils';

export interface SearchFilterCardProps {
  title: string;
  placeholder: string;
  max?: number;
  values: string[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onInputKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
  displayValue?: (value: string) => string;
  className?: string;
}

export function SearchFilterCard({
  title,
  placeholder,
  max = 5,
  values,
  inputValue,
  onInputChange,
  onInputKeyDown,
  onAdd,
  onRemove,
  displayValue,
  className,
}: SearchFilterCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const atMax = values.length >= max;

  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  function handleAdd() {
    onAdd();
    setIsOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    onInputKeyDown(e);
    if (e.key === 'Enter' && inputValue.trim() && !atMax) {
      setIsOpen(false);
    }
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <FormSectionHeader
        title={title}
        description={`Add up to ${max}`}
        titleClassName="text-title-sm font-semibold leading-tight text-foreground/90"
        descriptionClassName="hidden text-caption text-muted-foreground md:block"
        trailing={
          <Badge variant="outline" className="shrink-0 tabular-nums text-caption font-semibold">
            {values.length}/{max}
          </Badge>
        }
      />

      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((value) => (
            <Chip
              key={value}
              variant="neutral"
              className="inline-flex items-center gap-1 px-2 text-caption normal-case"
            >
              {displayValue ? displayValue(value) : value}
              <button
                type="button"
                aria-label={`Remove ${value}`}
                className="ml-0.5 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={() => onRemove(value)}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Chip>
          ))}
          {!atMax && !isOpen && (
            <button
              type="button"
              onClick={() => setIsOpen(true)}
              className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-border px-2 py-0.5 text-caption text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground md:hidden"
            >
              <Plus className="h-2.5 w-2.5" />
              Add
            </button>
          )}
        </div>
      )}

      {values.length === 0 && !isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-3 py-2 text-caption text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground md:hidden"
        >
          <Plus className="h-3 w-3" />
          Add {title.toLowerCase()}
        </button>
      )}

      <div className={cn('flex gap-2', !isOpen && 'max-md:hidden')}>
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={atMax}
          className="h-10 flex-1 border border-border text-sm font-medium"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!inputValue.trim() || atMax}
          onClick={handleAdd}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
